import React, { useState, useEffect } from 'react';
import {
  Phone,
  Video,
  Clock,
  AlertTriangle,
  Heart,
  Shield,
  Zap,
  MapPin,
  User,
  Calendar,
  MessageSquare,
  FileText,
  Activity,
  Thermometer,
  Eye,
  Stethoscope,
  Pill,
  X,
  CheckCircle,
  ArrowRight,
  Star,
  PhoneCall,
  PlayCircle,
  AlertCircle,
  Timer,
  Users,
  Award,
  BookOpen,
  Download,
  Share2
} from 'lucide-react';

const EmergencyCare = () => {
  const [emergencyType, setEmergencyType] = useState('');
  const [symptoms, setSymptoms] = useState([]);
  const [petDetails, setPetDetails] = useState({
    name: '',
    species: '',
    age: '',
    breed: ''
  });
  const [showCallModal, setShowCallModal] = useState(false);
  const [activeTab, setActiveTab] = useState('emergency');
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const emergencyTypes = [
    {
      id: 'breathing',
      name: 'Breathing Problems',
      icon: <Activity className="w-6 h-6" />,
      severity: 'critical',
      description: 'Difficulty breathing, choking, or respiratory distress'
    },
    {
      id: 'poisoning',
      name: 'Poisoning/Toxicity',
      icon: <AlertTriangle className="w-6 h-6" />,
      severity: 'critical',
      description: 'Suspected ingestion of toxic substances'
    },
    {
      id: 'trauma',
      name: 'Trauma/Injury',
      icon: <AlertTriangle className="w-6 h-6" />,
      severity: 'high',
      description: 'Accidents, falls, cuts, or physical injuries'
    },
    {
      id: 'seizure',
      name: 'Seizures',
      icon: <Zap className="w-6 h-6" />,
      severity: 'critical',
      description: 'Convulsions or neurological episodes'
    },
    {
      id: 'heatstroke',
      name: 'Heatstroke',
      icon: <Thermometer className="w-6 h-6" />,
      severity: 'high',
      description: 'Overheating or heat-related illness'
    },
    {
      id: 'bloat',
      name: 'Bloat/GDV',
      icon: <Activity className="w-6 h-6" />,
      severity: 'critical',
      description: 'Stomach distension or gastric torsion'
    }
  ];

  const commonSymptoms = [
    'Difficulty breathing', 'Vomiting', 'Diarrhea', 'Loss of consciousness',
    'Bleeding', 'Pale gums', 'Excessive drooling', 'Restlessness',
    'Weakness', 'Rapid heartbeat', 'High fever', 'Seizures'
  ];

  const emergencyVets = [
    {
      id: 1,
      name: 'Dr. Arjun Reddy',
      specialty: 'Emergency & Critical Care',
      experience: '12 years',
      rating: 4.9,
      reviews: 234,
      status: 'available',
      responseTime: '< 2 mins',
      image: '/api/placeholder/80/80'
    },
    {
      id: 2,
      name: 'Dr. Kavya Menon',
      specialty: 'Emergency Medicine',
      experience: '8 years',
      rating: 4.8,
      reviews: 189,
      status: 'available',
      responseTime: '< 3 mins',
      image: '/api/placeholder/80/80'
    },
    {
      id: 3,
      name: 'Dr. Rohit Sharma',
      specialty: 'Trauma & Surgery',
      experience: '15 years',
      rating: 4.9,
      reviews: 312,
      status: 'busy',
      responseTime: '~ 5 mins',
      image: '/api/placeholder/80/80'
    }
  ];

  const firstAidGuides = [
    {
      id: 1,
      title: 'Choking Emergency',
      duration: '2 min read',
      steps: 5,
      image: '/api/placeholder/200/120'
    },
    {
      id: 2,
      title: 'Poisoning Response',
      duration: '3 min read',
      steps: 7,
      image: '/api/placeholder/200/120'
    },
    {
      id: 3,
      title: 'Wound Care Basics',
      duration: '4 min read',
      steps: 6,
      image: '/api/placeholder/200/120'
    },
    {
      id: 4,
      title: 'Heat Stroke Treatment',
      duration: '3 min read',
      steps: 8,
      image: '/api/placeholder/200/120'
    }
  ];

  const handleSymptomToggle = (symptom) => {
    setSymptoms(prev => 
      prev.includes(symptom) 
        ? prev.filter(s => s !== symptom)
        : [...prev, symptom]
    );
  };

  const handleEmergencyCall = () => {
    setShowCallModal(true);
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getSeverityBg = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-50 border-red-200';
      case 'high': return 'bg-orange-50 border-orange-200';
      case 'medium': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      {/* Emergency Header */}
      <header className="bg-gradient-to-r from-red-600 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Emergency Care</h1>
                <p className="text-red-100">24/7 Immediate Veterinary Support</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">
                {currentTime.toLocaleTimeString()}
              </div>
              <div className="text-red-100 flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Emergency Team Online</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Emergency Action Buttons */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <button
            onClick={handleEmergencyCall}
            className="bg-red-600 hover:bg-red-700 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center space-x-4"
          >
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Phone className="w-8 h-8" />
            </div>
            <div className="text-left">
              <h3 className="text-xl font-bold">Emergency Call</h3>
              <p className="text-red-100">Connect instantly with emergency vet</p>
              <p className="text-sm text-red-200">Average response:  2 minutes</p>
            </div>
          </button>
          
          <button
            onClick={() => setShowCallModal(true)}
            className="bg-orange-600 hover:bg-orange-700 text-white p-6 rounded-2xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center space-x-4"
          >
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <Video className="w-8 h-8" />
            </div>
            <div className="text-left">
              <h3 className="text-xl font-bold">Emergency Video Call</h3>
              <p className="text-orange-100">Visual assessment & guidance</p>
              <p className="text-sm text-orange-200">HD video consultation</p>
            </div>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm">
            <button
              onClick={() => setActiveTab('emergency')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'emergency'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Emergency Assessment
            </button>
            <button
              onClick={() => setActiveTab('vets')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'vets'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              Emergency Vets
            </button>
            <button
              onClick={() => setActiveTab('firstaid')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'firstaid'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-700 hover:text-red-600'
              }`}
            >
              First Aid Guides
            </button>
          </div>
        </div>

        {/* Emergency Assessment Tab */}
        {activeTab === 'emergency' && (
          <div className="space-y-6">
            {/* Pet Details */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pet Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Pet Name"
                  value={petDetails.name}
                  onChange={(e) => setPetDetails({...petDetails, name: e.target.value})}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <select
                  value={petDetails.species}
                  onChange={(e) => setPetDetails({...petDetails, species: e.target.value})}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select Species</option>
                  <option value="dog">Dog</option>
                  <option value="cat">Cat</option>
                  <option value="bird">Bird</option>
                  <option value="rabbit">Rabbit</option>
                  <option value="other">Other</option>
                </select>
                <input
                  type="text"
                  placeholder="Age"
                  value={petDetails.age}
                  onChange={(e) => setPetDetails({...petDetails, age: e.target.value})}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Breed"
                  value={petDetails.breed}
                  onChange={(e) => setPetDetails({...petDetails, breed: e.target.value})}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Emergency Type Selection */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What type of emergency is this?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {emergencyTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setEmergencyType(type.id)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      emergencyType === type.id
                        ? 'border-red-500 bg-red-50'
                        : `border-gray-200 hover:border-gray-300 ${getSeverityBg(type.severity)}`
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSeverityColor(type.severity)} text-white`}>
                        {type.icon}
                      </div>
                      <h4 className="font-semibold text-gray-900">{type.name}</h4>
                    </div>
                    <p className="text-sm text-gray-600 text-left">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Symptoms Selection */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Select symptoms you're observing</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {commonSymptoms.map(symptom => (
                  <button
                    key={symptom}
                    onClick={() => handleSymptomToggle(symptom)}
                    className={`p-3 rounded-lg border text-sm transition-all ${
                      symptoms.includes(symptom)
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            </div>

            {/* Emergency Assessment Result */}
            {(emergencyType || symptoms.length > 0) && (
              <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-2xl p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Emergency Assessment Complete</h3>
                    <p className="text-red-100">Based on your inputs, immediate veterinary attention is recommended</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleEmergencyCall}
                    className="bg-white text-red-600 px-6 py-3 rounded-lg font-semibold hover:bg-red-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Call Emergency Vet Now</span>
                  </button>
                  <button className="border border-white/30 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/10 transition-colors flex items-center justify-center space-x-2">
                    <Video className="w-5 h-5" />
                    <span>Start Video Call</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Emergency Vets Tab */}
        {activeTab === 'vets' && (
          <div className="space-y-6">
            {emergencyVets.map(vet => (
              <div key={vet.id} className="bg-white rounded-2xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-600 rounded-full flex items-center justify-center">
                      <User className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{vet.name}</h3>
                        <div className={`w-2 h-2 rounded-full ${vet.status === 'available' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                      </div>
                      <p className="text-sm text-gray-600">{vet.specialty}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <div className="flex items-center space-x-1">
                          <Award className="w-4 h-4" />
                          <span>{vet.experience}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span>{vet.rating} ({vet.reviews})</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Timer className="w-4 h-4" />
                          <span>{vet.responseTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      vet.status === 'available' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {vet.status === 'available' ? 'Available Now' : 'Busy'}
                    </div>
                    <button
                      disabled={vet.status !== 'available'}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 ${
                        vet.status === 'available'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      <span>Emergency Call</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* First Aid Guides Tab */}
        {activeTab === 'firstaid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {firstAidGuides.map(guide => (
              <div key={guide.id} className="bg-white rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-32 bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
                  <BookOpen className="w-12 h-12 text-red-600" />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{guide.title}</h3>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{guide.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="w-4 h-4" />
                      <span>{guide.steps} steps</span>
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    <button className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2">
                      <PlayCircle className="w-4 h-4" />
                      <span>Read Guide</span>
                    </button>
                    <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Emergency Call Modal */}
      {showCallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Emergency Call</h3>
                <button
                  onClick={() => setShowCallModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-10 h-10 text-white animate-pulse" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Connecting to Emergency Vet</h4>
                <p className="text-gray-600">Please stay on the line. A veterinarian will be with you shortly.</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">Emergency Priority</p>
                    <p className="text-sm text-red-600">You're in the emergency queue</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">Estimated Wait Time</p>
                    <p className="text-sm text-blue-600">Less than 2 minutes</p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCallModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button className="flex-1 bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2">
                  <PhoneCall className="w-4 h-4" />
                  <span>Continue Call</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyCare;